// Typ: ClassifyTriangleByAnglesOnlyTask
// Dział 6 → Trójkąty → Podtemat 1 (własności/klasyfikacja)
// Cel: Na podstawie boków a,b,c określ rodzaj po kątach: ostrokątny / prostokątny / rozwartokątny

import SelectInput from "../../../../components/inputs/SelectInput";
import { InlineMath, BlockMath } from "react-katex";

// ─── helpers ──────────────────────────────────────────────────────────────────
const rnd = (a,b) => Math.floor(Math.random()*(b-a+1))+a;
const pick = (arr) => arr[Math.floor(Math.random()*arr.length)];
const shuffle = (arr) => arr.sort(()=>Math.random()-0.5);

function classifyByAngles(a,b,c){
  const [x,y,z] = [a,b,c].sort((p,q)=>p-q); // x ≤ y ≤ z
  const s = x*x + y*y;
  const z2 = z*z;
  if (s === z2) return "prostokatny";
  if (s >  z2) return "ostrokatny";
  return "rozwartokatny";
}

// ─── generator ─────────────────────────────────────────────────────────────────
// opcjonalnie można wymusić: { bucket: "right"|"acute"|"obtuse" }
export function generateTask(opts = {}) {
  let a,b,c;
  const bucket = opts.bucket || pick(["right","acute","obtuse"]);

  switch (bucket) {
    case "right": {
      const triples = [[3,4,5],[5,12,13],[6,8,10],[7,24,25],[8,15,17]];
      [a,b,c] = pick(triples);
      break;
    }
    case "acute": {
      const sets = [[5,6,7],[6,7,8],[7,8,9],[8,9,10]]; // s > z^2
      [a,b,c] = pick(sets);
      break;
    }
    case "obtuse": {
      const sets = [[6,8,13],[7,10,12],[9,10,18]]; // s < z^2
      [a,b,c] = pick(sets);
      break;
    }
    default: {
      [a,b,c] = [5,6,7];
    }
  }

  // przetasuj kolejność, żeby nie zdradzać najdłuższego
  [a,b,c] = shuffle([a,b,c]);

  return { type: "ClassifyTriangleByAnglesOnlyTask", data: { a,b,c } };
}

// ─── renderTask ────────────────────────────────────────────────────────────────
export function renderTask({ data }) {
  const { a,b,c } = data;
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        Na podstawie boków
        <InlineMath math={`\\;a=${a},\\; b=${b},\\; c=${c}`} />
        {" "}określ, czy trójkąt jest:
        {" "}ostrokątny, prostokątny czy rozwartokątny.
      </div>
    </div>
  );
}

// ─── renderInput ───────────────────────────────────────────────────────────────
export function renderInput(_, userAnswer, onChange, disabled) {
  const val = userAnswer ?? "";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
      <span>Rodzaj po kątach:</span>
      <SelectInput
        value={val}
        onChange={(e)=> onChange(e.target.value)}
        disabled={disabled}
        options={[
          { value:"ostrokatny",  label:"ostrokątny" },
          { value:"prostokatny", label:"prostokątny" },
          { value:"rozwartokatny", label:"rozwartokątny" }
        ]}
      />
    </div>
  );
}

// ─── checkAnswer ───────────────────────────────────────────────────────────────
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const { a,b,c } = data;
  const expected = classifyByAngles(a,b,c);
  return userAnswer === expected;
}

// ─── renderAnswer ──────────────────────────────────────────────────────────────
export function renderAnswer({ data }) {
  const { a,b,c } = data;
  const map = {
    ostrokatny: "ostrokątny",
    prostokatny: "prostokątny",
    rozwartokatny: "rozwartokątny"
  };
  const ans = map[classifyByAngles(a,b,c)];
  return <div>{ans}</div>;
}

// ─── renderExplanation ─────────────────────────────────────────────────────────
export function renderExplanation({ data }) {
  const { a, b, c } = data;

  // porządkujemy boki rosnąco: x ≤ y ≤ z (z — najdłuższy bok)
  const [x, y, z] = [a, b, c].sort((p, q) => p - q);

  const x2 = x * x;
  const y2 = y * y;
  const z2 = z * z;
  const sum = x2 + y2;

  const cmp = sum === z2 ? "=" : (sum > z2 ? ">" : "<");
  const comment =
    sum === z2
      ? "Suma kwadratów krótszych równa kwadratowi najdłuższego → trójkąt prostokątny."
      : sum > z2
      ? "Suma kwadratów krótszych większa niż kwadrat najdłuższego → trójkąt ostrokątny."
      : "Suma kwadratów krótszych mniejsza niż kwadrat najdłuższego → trójkąt rozwartokątny.";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>
        <strong>Krok 1.</strong> Porządkujemy boki rosnąco i
        <span>{" "}</span>oznaczamy <InlineMath math={"x\\le y\\le z"} />{" "}
        (gdzie <InlineMath math={"z"} /> to najdłuższy bok, czyli
        <InlineMath math={"\\,z=\\max\\{a,b,c\\}"} />).
      </div>
      {/* Po podstawieniu konkretnych liczb */}
      <BlockMath math={`${x}\\le ${y}\\le ${z}`} />
      <div style={{ marginTop: 8 }}>
        <strong>Krok 2.</strong> Stosujemy „odwrócone” twierdzenie Pitagorasa.
      </div>

      {/* Pokaż kwadraty liczbowo */}
      <BlockMath math={`${x}^{2} = ${x}^{2} = ${x2}`} />
      <BlockMath math={`${y}^{2} = ${y}^{2} = ${y2}`} />
      <BlockMath math={`${z}^{2} = ${z}^{2} = ${z2}`} />

      {/* Pokaż sumę i porównanie */}
      <BlockMath math={`${x}^{2}+${y}^{2} = ${x2}+${y2} = ${sum}`} />
      <BlockMath math={`${sum}\\;${cmp}\\;${z2}`} />

      <div>{comment}</div>
    </div>
  );
}

// ─── renderUserAnswer ──────────────────────────────────────────────────────────
export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const map = {
    ostrokatny: "ostrokątny",
    prostokatny: "prostokątny",
    rozwartokatny: "rozwartokątny"
  };
  return <div>{map[userAnswer] || <i>(brak odpowiedzi)</i>}</div>;
}

// ─── default export ────────────────────────────────────────────────────────────
export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
